// frontend\src\components\analytics\AnalyticsInventoryValueHistory.tsx
import React, { useEffect, useState, useCallback } from 'react';
import api from '../../utils/api';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend, Filler, ScriptableContext, TooltipItem } from 'chart.js';
import { Line } from 'react-chartjs-2';
import ErrorBoundary from '../ErrorBoundary';
import LoadingSpinner from '../LoadingSpinner';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend, Filler);

interface ValueSeriesPoint {
  date: string;
  total: number | null;
  byWarehouse: { [warehouse: string]: number | null };
}

interface InventoryValueHistoryResponse {
  series: ValueSeriesPoint[];
  warehouses: string[];
  start_date: string;
  end_date: string;
}

interface AnalyticsInventoryValueHistoryProps {
  startDate: string;
  endDate: string;
}

const WAREHOUSE_COLORS = ['#E07856', '#4FA37C', '#8E6FC9', '#E3B341', '#56A6C9', '#D8689B', '#7A9E5F', '#7B8794'];

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);

const accentGradient = (context: ScriptableContext<'line'>) => {
  const { ctx, chartArea } = context.chart;
  if (!chartArea) return 'rgba(59, 125, 216, 0.08)';
  const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
  gradient.addColorStop(0, 'rgba(59, 125, 216, 0.16)');
  gradient.addColorStop(1, 'rgba(59, 125, 216, 0)');
  return gradient;
};

const AnalyticsInventoryValueHistory: React.FC<AnalyticsInventoryValueHistoryProps> = ({ startDate, endDate }) => {
  const [data, setData] = useState<InventoryValueHistoryResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showWarehouses, setShowWarehouses] = useState(true);

  const fetchData = useCallback(async () => {
    if (!startDate || !endDate) return;
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/inventory-value-history', {
        params: { start_date: startDate, end_date: endDate },
      });
      setData(response.data);
    } catch (err: any) {
      console.error('Inventory value history fetch error:', err);
      setError(err.response?.data?.error || 'Failed to fetch inventory value history');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) return <div className="analytics-card"><LoadingSpinner /></div>;
  if (error) return <div className="analytics-error-text">Error: {error}</div>;
  if (!data) return null;

  const { series, warehouses } = data;
  const labels = series.map((d) => d.date);

  // Only chart warehouses that ever held value in the period
  const activeWarehouses = warehouses.filter((w) =>
    series.some((d) => (d.byWarehouse[w] ?? 0) > 0)
  );

  const chartData = {
    labels,
    datasets: [
      {
        label: 'Total value',
        data: series.map((d) => d.total),
        borderColor: '#3B7DD8',
        borderWidth: 2.5,
        backgroundColor: accentGradient,
        fill: true,
        pointStyle: 'line' as const,
        pointRadius: 0,
        tension: 0.25,
        spanGaps: true,
        order: 2,
      },
      ...(showWarehouses
        ? activeWarehouses.map((w, i) => ({
            label: w,
            data: series.map((d) => d.byWarehouse[w]),
            borderColor: WAREHOUSE_COLORS[i % WAREHOUSE_COLORS.length],
            borderWidth: 1.5,
            backgroundColor: 'rgba(0,0,0,0)',
            fill: false,
            pointStyle: 'line' as const,
            pointRadius: 0,
            tension: 0.25,
            spanGaps: true,
            order: 3,
          }))
        : []),
    ],
  };

  return (
    <div className="analytics-card">
      <div className="analytics-card-header">
        <h3>Inventory Value Over Time</h3>
        <label className="analytics-toggle-label">
          <input
            type="checkbox"
            checked={showWarehouses}
            onChange={(e) => setShowWarehouses(e.target.checked)}
          />
          Show warehouse breakdown
        </label>
      </div>
      <ErrorBoundary>
        {series.some((d) => d.total !== null) ? (
          <div style={{ minHeight: '400px' }}>
            <Line
              data={chartData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                interaction: { mode: 'index', intersect: false },
                plugins: {
                  legend: {
                    position: 'bottom',
                    labels: {
                      font: { family: "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif", size: 13 },
                      color: '#5A5A56',
                      padding: 20,
                      usePointStyle: true,
                    },
                  },
                  tooltip: {
                    backgroundColor: '#ffffff',
                    titleColor: '#1C1C1A',
                    bodyColor: '#1C1C1A',
                    borderColor: '#E8E8E4',
                    borderWidth: 1,
                    cornerRadius: 10,
                    padding: 12,
                    boxPadding: 4,
                    filter: (item: TooltipItem<'line'>) => item.raw !== null,
                    callbacks: {
                      label: (context: TooltipItem<'line'>) =>
                        `${context.dataset.label}: ${formatCurrency(Number(context.raw) || 0)}`,
                    },
                  },
                },
                scales: {
                  y: {
                    beginAtZero: true,
                    border: { display: false },
                    grid: { color: 'rgba(0, 0, 0, 0.05)' },
                    ticks: {
                      color: '#8F8F8A',
                      font: { family: "'DM Mono', monospace", size: 11 },
                      callback: (value: number | string) => formatCurrency(Number(value)),
                    },
                    title: {
                      display: true,
                      text: 'Inventory Value (USD)',
                      color: '#5A5A56',
                      font: { family: "'DM Sans', sans-serif", size: 12 },
                    },
                  },
                  x: {
                    border: { display: false },
                    grid: { display: false },
                    ticks: {
                      color: '#8F8F8A',
                      font: { family: "'DM Sans', sans-serif", size: 11 },
                      maxRotation: 45,
                      autoSkip: true,
                      maxTicksLimit: 20,
                    },
                  },
                },
              }}
            />
          </div>
        ) : (
          <p className="analytics-empty-text">
            No inventory value history recorded yet for the chosen period. Values are captured daily going forward.
          </p>
        )}
      </ErrorBoundary>
    </div>
  );
};

export default AnalyticsInventoryValueHistory;
